import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PersonDto } from 'api/actor';
import { ReferentialKey } from 'api/referential';
import { useAppDispatch } from 'app/hooks';
import { getMultipleReferentialsByKeys } from 'features/referentials/slices/getMultipleReferentialsByKeysThunk';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { useSingleFlight } from 'hooks/useSingleFlight';
import { actorsAPI, contractsAPI } from 'services/swagger-api';
import { useGroupContractProcess } from '../contexts/GroupContractProcessContext';
import { GroupContractStepIdentifier } from '../types/enums';
import { SubscriberPicker } from '../components/SubscriberPicker';
import { GroupContractScope } from '../api/groupContractSubscribersApi';

export const SubscriberStep = () => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common, NsKey.ActorForm]);
  const { groupContractId, refresh, buildStepRoute } = useGroupContractProcess();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [initialPerson, setInitialPerson] = useState<PersonDto | null>(null);
  // PlateformeId du contrat : propagée au SubscriberPicker pour que toute Personne créée
  // soit rattachée à cette OU (Personne.PlateformeId), ce qui la rendra visible par les
  // gestionnaires corporate de cette plateforme.
  const [contractPlateformeId, setContractPlateformeId] = useState<string | undefined>(undefined);
  const [scope, setScope] = useState<GroupContractScope>(GroupContractScope.PersonneMorale);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Référentiels nécessaires au formulaire de création de personne
  useEffect(() => {
    dispatch(getMultipleReferentialsByKeys([
      ReferentialKey.Civility,
      ReferentialKey.Nationality,
      ReferentialKey.Country,
      ReferentialKey.LegalForm,
      ReferentialKey.CompanyRole,
    ]));
  }, [dispatch]);

  // Pré-charge le souscripteur déjà sélectionné côté serveur (si on revient sur l'étape).
  // À défaut, on tente de pré-cocher la PM corporate de l'OU propriétaire du contrat.
  useEffect(() => {
    let cancelled = false;
    const loadCurrentSubscriber = async () => {
      try {
        const contract = await contractsAPI.getGroupContractByID({ groupContractId });
        const contractScope = (contract as { scope?: GroupContractScope }).scope
          ?? GroupContractScope.PersonneMorale;
        if (!cancelled) {
          setContractPlateformeId(contract.plateformeId ?? undefined);
          setScope(contractScope);
        }

        // On ne pré-remplit QUE depuis un souscripteur réellement attaché au contrat.
        // Pas de pré-sélection de la PM corporate : le souscripteur doit provenir de
        // l'ensemble éligible (cabinet / portefeuille partagé), choisi via le picker.
        const subscriberId = contract.subscriber?.id;
        if (subscriberId) {
          const personResponse = await actorsAPI.getPersonByID({ personId: subscriberId });
          if (!cancelled) setInitialPerson(personResponse.person);
          return;
        }

        if (!cancelled) setInitialPerson(null);
      } catch {
        if (!cancelled) setInitialPerson(null);
      } finally {
        if (!cancelled) setIsLoadingInitial(false);
      }
    };
    loadCurrentSubscriber();
    return () => { cancelled = true; };
  }, [groupContractId]);

  const submitPerson = useCallback(async (personId: string) => {
    // Vue résumé read-only : si l'utilisateur confirme la même personne déjà attachée,
    // on saute l'appel API (write inutile) et on navigue directement.
    if (personId === initialPerson?.id) {
      navigate(buildStepRoute(GroupContractStepIdentifier.Products));
      return;
    }

    setError(null);
    try {
      await contractsAPI.setGroupContractSubscriber({
        groupContractId,
        setSubscriberCommand: { subscriberId: personId },
      });
      await refresh();
      navigate(buildStepRoute(GroupContractStepIdentifier.Products));
    } catch (err: any) {
      setError(err?.message || t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_STEP.ERROR_SAVE'));
    }
  }, [groupContractId, refresh, navigate, buildStepRoute, t, initialPerson]);

  // Un seul PUT /souscripteur en vol : les sélections/clics répétés sont ignorés
  // (sinon writes concurrents → conflit de concurrence optimiste côté back).
  const { run: handlePersonReady, isInFlight: isSubmitting } = useSingleFlight(submitPerson);

  if (isLoadingInitial) {
    return (
      <div className="text-center text-sm text-gray-500 py-8">
        {t('GROUP_CONTRACT_PROCESS.SUBSCRIBER_STEP.LOADING')}
      </div>
    );
  }

  return (
    <>
      <SubscriberPicker
        contractId={groupContractId}
        scope={scope}
        initialPerson={initialPerson}
        onPersonReady={handlePersonReady}
        isSubmitting={isSubmitting}
        plateformeId={contractPlateformeId}
      />
      {error && (
        <div className="max-w-md mx-auto mt-4 text-center text-sm text-red-600">
          {error}
        </div>
      )}
    </>
  );
};
